<template>
  <v-switch :model-value="value"
            v-bind="$attrs"
            inset
            :ripple="false"
            :disabled="disabled || loading"
            :loading="loading"
            class="AppToggle"
            @update:model-value="$emit('input', !!$event)"
  >
    <template #thumb>
      <div class="v-switch__thumb">
        <template v-if="loading">
          <v-progress-circular indeterminate size="16" width="2" color="primary"></v-progress-circular>
        </template>
        <template v-else>
          <app-material-icon
            :name="value ? 'check' : 'xmark'"
            :size="iconSize"
            type="fa"
            :color="value ? checkedIconColor : 'grey-400'"
          />
        </template>
      </div>
    </template>
  </v-switch>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

/**
 * It's used to switch the state of a single option. The inline label of "Switch" should be displayed clearly, e.g. Disable/Enable, Disallow/Allow etc.
 * All vuetify props are available
 * @displayName Toggle
 */

export default defineComponent({
  name: 'AppToggle',
  inheritAttrs: false,
  props: {
    /**
     * The v-model bound value
     * */
    value: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    /**
     * Displays circular progress bar.
     * */
    loading: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    /**
     * Disable the input
     * */
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    iconColor: {
      type: String,
      default: null,
    },
    iconSize: {
      type: String,
      default: "xs",
    },
  },
  computed: {
    checkedIconColor (): string {
      return this.iconColor ?? 'primary'
    },
  },
})

</script>

<style scoped lang="scss">
.AppToggle {
  :deep(.v-switch__track) {
    height: 28px;
    min-width: 48px;
    background-color: rgb(var(--v-theme-grey-400));
    opacity: .6;

    &.bg-primary {
      opacity: 1;
    }
  }

  :deep(.v-switch__thumb) {
    transform: scale(1);
    height: 22px;
    width: 22px;
  }
}
</style>
